import React, { useEffect, useState } from 'react';
import { View } from 'react-native';
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

import Container from '../atoms/Container/Container';
import drawerHoc from '../../../../components/drawerHOC/DrawerHOC';
import { useDispatch, useSelector } from 'react-redux';
import { tenantActions } from '../../redux/tenant.actions';

import { Modal, Box, Typography, Button } from "@mui/material";

import ProgressCircle from "./components/ProgressCircle";
import DepositWizard from "./DepositWizard";
import DepositDetails from './depositDetails';
import TenantDetails from '../molecules/specialCondition/adddeposit/TenantDetails';
import LandlordDetails, { isLandlordTitleMissing } from '../molecules/specialCondition/adddeposit/LandlordDetails';
import RelevantPersonDetails from '../molecules/specialCondition/adddeposit/RelevantPersonDetails';
import ConfirmationStep from '../molecules/specialCondition/adddeposit/ConfirmationStep';
import { navigate } from '../../../../navigation/RootNavigation';

// --------------------------------------------------
// STEP DEFINITIONS
// --------------------------------------------------
const steps = [
  { label: "01 Deposit", subLabel: "Deposit details" },
  { label: "02 Tenant", subLabel: "Tenant Details" },
  { label: "03 Landlord", subLabel: "Landlord Details" },
  { label: "04 Relevant Person", subLabel: "Relevant Person Details" },
  { label: "05 Confirmation", subLabel: "Confirmation Details" },
];

// --------------------------------------------------
// TDS URL
// --------------------------------------------------
const TDS_URL = 'https://sandbox.tenancydepositscheme.com/log-in/';

// --------------------------------------------------
// 1️⃣ DEPOSIT SCHEMA (STEP 0)
// --------------------------------------------------
const depositSchema = yup.object().shape({
  rental_amount: yup.number().required("Rent amount is required"),
  rental_frequency: yup.string().required("Rent period is required"),
  tenancy_deposit_amount: yup.number().required("Deposit amount is required"),
  tenancy_deposit_amount_protect: yup.number().required("Amount to protect is required"),
  holding_desposit_received_date: yup.date().required("Deposit received date is required"),
  tenancy_start_date: yup.date().required("Tenancy start date is required"),
  tenancy_end_date: yup.date().required("Expected end date is required"),
  number_of_tenants: yup.number().required("Number of tenants is required"),
  property_name: yup.string().required("Property name is required"),
});

// --------------------------------------------------
// 2️⃣ TENANT SCHEMA (STEP 1)
// --------------------------------------------------
const tenantSchema = yup.object().shape({
  is_organizational_tenant: yup
    .string()
    .required("Please select an option"),

  is_relative_person: yup
    .string()
    .when("is_organizational_tenant", {
      is: (val) => val === "NO",
      then: (s) => s.required("Please select yes or no"),
      otherwise: (s) => s.notRequired(),
    }),

  agreement: yup
    .string()
    .when("is_organizational_tenant", {
      is: (val) => val === "NO",
      then: (s) =>
        s.oneOf(["agree"], "You must select agree to continue")
        .required("Agreement is required"),
      otherwise: (s) => s.notRequired(),
    }),
});

// --------------------------------------------------
// 3️⃣ RELEVANT PERSON SCHEMA (STEP 3)
// --------------------------------------------------
const relevantPersonSchema = yup.object().shape({
  title: yup.string().required("Title is required"),
  name: yup.string().required("First name is required"),
  surname: yup.string().required("Surname is required"),
  email: yup.string().email().required("Email is required"),
  code: yup.string().required("Code is required"),
  phone: yup.string().required("Phone number is required"),
  address: yup.string().required("Address is required"),
});

// --------------------------------------------------
// DYNAMIC SCHEMA MAP
// --------------------------------------------------
const schemaByStep = {
  0: depositSchema,
  1: tenantSchema,
  2: null, // Landlord has no react-hook-form validation
  3: relevantPersonSchema,
  4: null, // confirmation has no validation
};

// --------------------------------------------------
// DEFAULT VALUES
// --------------------------------------------------
const defaultValues = {
  rental_amount: "",
  rental_frequency: "PCM",
  tenancy_deposit_amount: "",
  tenancy_deposit_amount_protect: "",
  holding_desposit_received_date: new Date(),
  tenancy_start_date: new Date(),
  tenancy_end_date: new Date(),
  number_of_tenants: 1,
  property_name: "",
  is_organizational_tenant: "",
  is_relative_person: "",
  agreement: "",
  rp_name: "",
  rp_email: "",
  contactAddress: "",
};

// --------------------------------------------------
// MAIN COMPONENT
// --------------------------------------------------
const RegisterDeposit = (props) => {
  const dispatch = useDispatch();

  const openFlow = useSelector((state) => state?.tenant?.flowIdInfo);
  const depositProtectionData = useSelector((state) => state?.tenant?.depositProtectionData);

  const [payload, setPayload] = useState({});
  const [activeStep, setActiveStep] = useState(0);
  const [endDateOption, setEndDateOption] = useState("custom");
  const [showOrgPopup, setShowOrgPopup] = useState(false);
  const [showLandlordTitlePopup, setShowLandlordTitlePopup] = useState(false);

  useEffect(() => {
    if (depositProtectionData) {
      setPayload((current) => ({ ...current, ...depositProtectionData }));
    }
  }, [depositProtectionData]);

  useEffect(() => {
    if (depositProtectionData?.batch_id) {
      setActiveStep(4);
    }
  }, [depositProtectionData?.batch_id]);

  // --------------------------------------------------
  // INIT FORM WITH DYNAMIC RESOLVER BY STEP
  // --------------------------------------------------
  const {
    control,
    watch,
    trigger,
    getValues,
    handleSubmit,
    setValue,
    reset,      
    formState: { errors },
  } = useForm({
    defaultValues,
    resolver: schemaByStep[activeStep] ? yupResolver(schemaByStep[activeStep]) : undefined,
  });

  // --------------------------------------------------
  // Fetch Flow ID details
  // --------------------------------------------------
  useEffect(() => {
    if (props?.route?.params?.id) {
      dispatch(tenantActions.getFlowInfo(props.route.params.id));
    }
  }, [props?.route?.params?.id]);

  // --------------------------------------------------
  // Prefill when API returns
  // --------------------------------------------------
  useEffect(() => {
    if (openFlow) {
      const mappedValues = {
        rental_amount: openFlow?.open_flow_rental_offer?.rental_amount || "",
        rental_frequency: openFlow?.open_flow_rental_offer?.rental_frequency || "PCM",
        tenancy_deposit_amount: openFlow?.open_flow_rental_offer?.tenancy_deposit_amount || "",
        tenancy_deposit_amount_protect: openFlow?.open_flow_rental_offer?.tenancy_deposit_amount || "",
        holding_desposit_received_date:
          openFlow?.open_flow_holding_deposit?.holding_desposit_received_date
            ? new Date(openFlow?.open_flow_holding_deposit?.holding_desposit_received_date)
            : new Date(),
        tenancy_start_date:
          openFlow?.open_flow_rental_offer?.tenancy_start_date
            ? new Date(openFlow?.open_flow_rental_offer?.tenancy_start_date)
            : new Date(),
        tenancy_end_date:
          openFlow?.open_flow_rental_offer?.tenancy_end_date
            ? new Date(openFlow?.open_flow_rental_offer?.tenancy_end_date)
            : new Date(),
        number_of_tenants: openFlow?.tenants?.length || 1,
        property_name: openFlow?.property?.property_name || "",
      };

      Object.entries(mappedValues).forEach(([key, val]) => setValue(key, val));
    }
  }, [openFlow]);


  // --------------------------------------------------
  // NEXT BUTTON LOGIC
  // --------------------------------------------------
  const handleNext = async () => {
    if (schemaByStep[activeStep]) {
      const valid = await trigger(); // validate only current step
      if (!valid) return;
    }

    const values = getValues();
    const currentPayload = { ...payload, ...values };

    // STEP 1 (Tenant step)
    if (activeStep === 1) {
      if (values?.is_organizational_tenant === "YES") {
        setShowOrgPopup(true);
        return;
      }
      setPayload(currentPayload);
      setActiveStep(2); // Move to Landlord step (Step 2)
      return;
    }

    // STEP 2 (Landlord step)
    if (activeStep === 2) {
      const landlords = Array.isArray(openFlow?.open_flow_landlords) ? openFlow.open_flow_landlords : [];
      const missing = landlords.filter(isLandlordTitleMissing);
      if (missing.length > 0) {
        setShowLandlordTitlePopup(true);
        return;
      }

      // If tenant specified NO to relevant person, skip to confirmation (Step 4)
      if (currentPayload?.is_relative_person === "NO" || values?.is_relative_person === "NO") {
        setActiveStep(4);
        return;
      }

      setActiveStep(3); // Proceed to Relevant Person (Step 3)
      return;
    }

    // STEP 3 (Relevant Person step)
    if (activeStep === 3) {
      const relDetails = {
        title: values.title,
        name: values.name,
        surname: values.surname,
        email: values.email,
        code: values.code,
        phone: values.phone,
        address: values.address,
      };
      setPayload({
        ...currentPayload,
        relativePersonDetails: relDetails,
      });
      setActiveStep(4); // Move to Confirmation (Step 4)
      return;
    }

    setPayload(currentPayload);
    setActiveStep((prev) => prev + 1);
  };

  const handlePrev = () => {
    if (activeStep === 4 && payload?.is_relative_person === "NO") {
      setActiveStep(2); // Confirmation back to Landlord
    } else if (activeStep > 0) {
      setActiveStep((prev) => prev - 1);
    }
  };

  // --------------------------------------------------
  // RENDER STEP CONTENT
  // --------------------------------------------------
  const renderStep = () => {
    switch (activeStep) {
      case 0:
        return (
          <DepositDetails
            control={control}
            watch={watch}
            setValue={setValue}
            errors={errors}
            reset={reset}       
            payload={payload}  
            openFlow={openFlow}
            endDateOption={endDateOption}
          />
        );

      case 1:
        return (
          <TenantDetails
            control={control}
            watch={watch}
            setValue={setValue}
            errors={errors}
            openFlow={openFlow}
            reset={reset}       
            payload={payload}  
            setShowOrgPopup={setShowOrgPopup}
          />
        );

      case 2:
        return (
          <LandlordDetails
            openFlow={openFlow}
            payload={payload}
            showMissingTitlePopup={showLandlordTitlePopup}
            setShowMissingTitlePopup={setShowLandlordTitlePopup}
          />
        );

      case 3:
        return (
          <RelevantPersonDetails
            control={control}
            watch={watch}
            errors={errors}
            setValue={setValue}
            openFlow={openFlow}
            reset={reset}       
            payload={payload}  
          />
        );

      case 4:
        return (
          <ConfirmationStep
            control={control}
            watch={watch}
            errors={errors}
            setValue={setValue}
            openFlow={openFlow}
            reset={reset}       
            payload={payload}
            handlePrev={handlePrev}
          />
        );

      default:
        return null;
    }
  };

  // --------------------------------------------------
  // MAIN VIEW
  // --------------------------------------------------
  return (
    <Container style={{ fontWeight: 400 }}>

      <ProgressCircle props={props} />

      <DepositWizard
        steps={steps}
        activeStep={activeStep}
        onStepChange={setActiveStep}
        onClose={() => navigate("DepositProtectHomeSetup",{id:props?.route?.params?.id})}
        onNext={handleNext}
        onPrev={handlePrev}
      >
        {renderStep()}
      </DepositWizard>

      {/* ORGANIZATIONAL POPUP */}
      <Modal open={showOrgPopup} onClose={() => setShowOrgPopup(false)}>
        <Box
          sx={{
            width: 500,
            bgcolor: "background.paper",
            margin: "100px auto",
            padding: 4,
            borderRadius: 2,
            boxShadow: 6,
          }}
        >
          <Typography variant="h6" fontWeight={700} mb={2}>
            You will now be redirected to TDS
          </Typography>

          <Typography mb={2}>
            Organization option is not available in our app. Please continue on the TDS website.
          </Typography>

          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
            <Button variant="outlined" color="secondary" onClick={() => setShowOrgPopup(false)}>
              Go Back
            </Button>

            <Button
              variant="contained"
              color="error"
              onClick={() => window.open(TDS_URL, "_blank")}
            >
              Go to TDS website
            </Button>
          </Box>
        </Box>
      </Modal>

    </Container>
  );
};

export default drawerHoc({
  Component: RegisterDeposit,
  showBackButton: true,
  backNavigation: "CurrentOnboards",
});
